import * as React from "react"
import * as YouthCancer from "models/youthCancer";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from "@mui/material";
import { Link } from "react-scroll";
import { FilterContext } from "models/core/filter";
import { DashChip } from "components/common/DashChips";
import ReportApi from "api/reportApi";
import * as fileHelper from "helpers/fileHelper";
import { Download, Downloading } from "@mui/icons-material";
import { PatientsBySiteChart } from "charts/youthCancer/PatientsBySiteChart";
import { RemotenessChart } from "charts/youthCancer/RemotenessChart";
import { AgeAtDiagnosisChart } from "charts/common/AgeAtDiagnosisChart";
import { FertilityPreservationChart } from "charts/youthCancer/FertilityPreservationChart";
import { RecurrenceChart } from "charts/youthCancer/RecurrenceChart";
import { HadTreatmentChart } from "charts/youthCancer/HadTreatmentChart";
import { GPDetailsProvidedChart } from "charts/youthCancer/GPDetailsProvidedChart";
import Loader from "pages/shared/pageloader";


enum YouthCancerIndicatorAction {
	UserClickedDownloadIndicator,
	DownloadIndicatorSuccess,
	DownloadIndicatorFail
}

type Action =
	{ type: YouthCancerIndicatorAction.UserClickedDownloadIndicator, payload: number }
	| { type: YouthCancerIndicatorAction.DownloadIndicatorSuccess }
	| { type: YouthCancerIndicatorAction.DownloadIndicatorFail }


interface State {
	indicator: number
}

const initialState: State = {
	indicator: 0
}



const reducer = (state: State, action: Action): State => {
	const { type } = action;

	switch (type) {
		case YouthCancerIndicatorAction.UserClickedDownloadIndicator:
			return { ...state, ...{ indicator: action.payload } };

		case YouthCancerIndicatorAction.DownloadIndicatorSuccess:
		case YouthCancerIndicatorAction.DownloadIndicatorFail:
			return { ...state, ...{ indicator: 0 } };

		default:
			return state;
	}
}


export function YouthCancerDashboard() {
	const [localState, localDispatch] = React.useReducer(reducer, initialState)
	const { state, dispatch } = React.useContext(AppContext);


	const [showFilterChips, setFilterChips] = React.useState(true);

	const addFilter: YouthCancer.AddFilterFunc = async (key: keyof typeof YouthCancer.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await YouthCancer.computeChartData(YouthCancer.addFilter(state.page.dashboard as YouthCancer.IYouthCancerDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: YouthCancer.RemoveFilterFunc = async (key: keyof typeof YouthCancer.DashboardFilterKeys, value?: string) => {
		const filteredDashboard = await YouthCancer.computeChartData(YouthCancer.removeFilter(state.page.dashboard as YouthCancer.IYouthCancerDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
		YouthCancer.loadYouthCancerDashboard(state.page.dashboard as YouthCancer.IYouthCancerDashboard)
			.then((value) => {
				dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value })
			});
		return () => {
			YouthCancer.closeYouthCancerDashboard();
		}
	}, []);

	React.useEffect(() => {
		if (localState.indicator == 0)
			return;

		let ids = youthCancerChartData.level1.filteredReferralIdCsv;
		let alldata = false;


		const getYouthCancerIndicatorPatientList = async () => {
			try {
				const data = await ReportApi.getYouthCancerIndicatorPatientList(ids, alldata, localState.indicator);
				if (data.errorMessage) {
					console.error(data.errorMessage);
					localDispatch({ type: YouthCancerIndicatorAction.DownloadIndicatorFail })
				}
				localDispatch({ type: YouthCancerIndicatorAction.DownloadIndicatorSuccess })
				return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportPatientsElem")
			} catch {
				localDispatch({ type: YouthCancerIndicatorAction.DownloadIndicatorFail })
			}
		}

		getYouthCancerIndicatorPatientList();


	}, [localState.indicator]);


	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />

	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in YouthCancer.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(YouthCancer.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": YouthCancer.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})

	const youthCancerChartData = state.page.dashboard.chartData as YouthCancer.IChartData;

	const downloadBtn = (extractTypeId: number) => {
		const { indicator } = localState;

		if (indicator == extractTypeId)
			return <Downloading />

		return (
			<Download style={{ cursor: "pointer" }} onClick={() =>
				localDispatch({ type: YouthCancerIndicatorAction.UserClickedDownloadIndicator, payload: extractTypeId })} />
		)
	}

	// const ecog = {
	// 	numerator: 0,
	// 	denominator: 0,
	// 	percentage: 0
	// }

	// const stage = {
	// 	numerator: 0,
	// 	denominator: 0,
	// 	percentage: 0
	// }

	// if (youthCancerChartData.level1.totalDiagnoses > 0) {
	// 	ecog.numerator = youthCancerChartData.level1.totalDiagnoses - (youthCancerChartData.level2.ecogGroup.values[youthCancerChartData.level2.ecogGroup.labels.indexOf("Unk")] || 0);
	// 	ecog.denominator = youthCancerChartData.level1.totalDiagnoses
	// 	ecog.percentage = ecog.numerator / ecog.denominator * 100

	// 	stage.numerator = youthCancerChartData.level1.totalDiagnoses - (youthCancerChartData.level2.tnmStage.values[youthCancerChartData.level2.tnmStage.labels.indexOf("Unk")] || 0);
	// 	stage.denominator = youthCancerChartData.level1.totalDiagnoses;
	// 	stage.percentage = stage.numerator / stage.denominator * 100
	// }


	const dashboard = <>
		<div style={{
			position: "sticky",
			top: "64px",
			left: "10px",
			zIndex: 2,
			backgroundColor: "white"
		}}>


			{(showFilterChips ? <div style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
				{chips}
			</div> : null)}
		</div>



		<div style={{
			maxWidth: 1000,
			margin: "0 auto"
		}}>

			<div style={{
				textAlign: "right",
				paddingBottom: "10px",
				paddingTop: "10px",
				position: "sticky",
				zIndex: 1,
				top: "60px",
				backgroundColor: "white"
			}}>
				<Button onClick={() => localDispatch({ type: YouthCancerIndicatorAction.UserClickedDownloadIndicator, payload: 1 })}
					style={{
						backgroundColor: ("#1e88e5"),
						color: "white",
						alignItems: "right"
					}}>
					Download Patient Details
				</Button>
			</div>
			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Indicator</TableCell>
							<TableCell colSpan={2} align="center">My MDT(s)</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Number of youth cancer patients</TableCell>
							<TableCell colSpan={2} align="center">{`${youthCancerChartData.level2.totalDiagnoses}`}</TableCell>
						</TableRow>

						{/* <TableRow>
							<TableCell>Percentage of patients with ECOG</TableCell>
							<TableCell align = "center">{`${ecog.percentage.toFixed(0)}%`} <br/> {`(${ecog.numerator}/${ecog.denominator})`}</TableCell>
							<TableCell align = "center">-</TableCell> 
					</TableRow> */}

						{/* <TableRow>
							<TableCell colSpan={4}>
								<b>Diagnosis</b>
							</TableCell>
						</TableRow> */}

						<TableRow>
							<TableCell>Psychosocial care plan completed</TableCell>
							<TableCell align="center"> {`(${youthCancerChartData.level2.psychosocialCarePlanNum.numerator}/${youthCancerChartData.level2.psychosocialCarePlanNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.psychosocialCarePlanNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>

						{/* <TableRow>
							<TableCell colSpan={4}>
								<b>Surgical</b>
							</TableCell>
						</TableRow> */}
						<TableRow>
							<TableCell>Received psychosocial / supportive care</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.receivedPsychosocialCareNum.numerator}/${youthCancerChartData.level2.receivedPsychosocialCareNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.receivedPsychosocialCareNum.percentage.toFixed(1)}%`}</TableCell>

						</TableRow>
						<TableRow>
							<TableCell>Had fertility discussion</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.hadFertilityDiscussionNum.numerator}/${youthCancerChartData.level2.hadFertilityDiscussionNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.hadFertilityDiscussionNum.percentage.toFixed(1)}%`}</TableCell>

						</TableRow>
						<TableRow>
							<TableCell>Referred to fertility specialist</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.referralToFertilitySpecialistNum.numerator}/${youthCancerChartData.level2.referralToFertilitySpecialistNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.referralToFertilitySpecialistNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Underwent fertility preservation</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.fertilityPreservationNum.numerator}/${youthCancerChartData.level2.fertilityPreservationNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.fertilityPreservationNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Reviewed at treatment MDT</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.reviewedAtTreatmentMDTNum.numerator}/${youthCancerChartData.level2.reviewedAtTreatmentMDTNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.reviewedAtTreatmentMDTNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Psychosocial survivorship care plan given to patient</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.psychosocialSurvivorshipCarePlanNum.numerator}/${youthCancerChartData.level2.psychosocialSurvivorshipCarePlanNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.psychosocialSurvivorshipCarePlanNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Medical treatment plan given to GP</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.medicalTreatmentPlanToGPNum.numerator}/${youthCancerChartData.level2.medicalTreatmentPlanToGPNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.medicalTreatmentPlanToGPNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Enrolled in a medical trial</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.enrolledInMedicalTrialNum.numerator}/${youthCancerChartData.level2.enrolledInMedicalTrialNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.enrolledInMedicalTrialNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Enrolled in other research</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.enrolledInOtherResearchNum.numerator}/${youthCancerChartData.level2.enrolledInOtherResearchNum.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.enrolledInOtherResearchNum.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>One year survival^</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.oneYearSurvival.numerator}/${youthCancerChartData.level2.oneYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.oneYearSurvival.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Two year survival^</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.twoYearSurvival.numerator}/${youthCancerChartData.level2.twoYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.twoYearSurvival.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Five year survival^</TableCell>
							<TableCell align="center">{`(${youthCancerChartData.level2.fiveYearSurvival.numerator}/${youthCancerChartData.level2.fiveYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{`${youthCancerChartData.level2.fiveYearSurvival.percentage.toFixed(1)}%`}</TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>^Patients with less than one, two, and five years of data from diagnosis are excluded from the one, two, and five year survival indicators respectively.</i></p>
			</div>

		</div>

		<Grid container id="PatientCx">
			<Grid item xs={11}>
				<h1 style={{ marginLeft: "10px", marginBottom: "20px" }}>Patient Characteristics</h1>
			</Grid>

			<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
				<PatientsBySiteChart
					plotData={youthCancerChartData.level2.primarySite}
					activeFilters={state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn={removeFilter}
				/>
			</Grid>

			<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
				<HadTreatmentChart
					plotData={youthCancerChartData.level2.hadTreatment}
					activeFilters={state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn={removeFilter}
				/>
			</Grid>

			<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
				<FertilityPreservationChart
					plotData={youthCancerChartData.level2.fertilityPreservation}
					activeFilters={state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn={removeFilter}
				/>
			</Grid>

			<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<RecurrenceChart
					plotData={youthCancerChartData.level2.diagnosisRecurrence}
					activeFilters={state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn={removeFilter}
				/>
			</Grid>

			<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<AgeAtDiagnosisChart
					plotData={youthCancerChartData.level2.ageRange}
					activeFilters={state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn={removeFilter}
				/>
			</Grid>

			<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<RemotenessChart
					plotData={youthCancerChartData.level2.remoteness}
					activeFilters={state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn={removeFilter}
				/>
			</Grid>

			<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<GPDetailsProvidedChart
					plotData={youthCancerChartData.level2.gpDetailsProvided}
					activeFilters={state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn={removeFilter}
				/>
			</Grid>
		</Grid>

		<a id="exportPatientsElem" style={{ display: "none" }}></a>

	</>

	return (
		dashboard
	)


} 